import * as React from 'react'
import { GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'
import Image from 'next/image'

import { getAllFilesFrontMatter } from '~/lib/mdx'
import { TestimonialFrontMatter, PostFrontMatter } from '~/types'
import Container from '~/components/Container'
import TestimonialsSlider from '~/components/Testimonials'
import GrowerTestimonial from '~/components/GrowerTestimonial'
import ArticleList from '~/components/ArticleList'

const title = 'Growers - Mary AG'
const description = ''

export const getStaticProps: GetStaticProps = async () => {
  const testimonials = await getFilteredSortedPosts()
  const posts = await getFilteredSortedArticles()
  const data = {
    testimonials,
    header_section: {
      title: 'Start Your Growing Journey',
      sub_title: 'Grow Smarter',
      body: 'The Mary growing platform learns with each plant growth cycle, leading to bigger and better harvests.',
      sub_body:
        'Whether this is your first time growing, or you are an experienced gardener, you can benefit from the knowledge of our growing community. Data from each plant cycle is paired with your feedback to improve our automation.'
    },
    knowledge_section: {
      title: "Grower's Knowledge",
      posts: posts
    }
  }
  return { props: { data } }
}

export default function GrowersPage(props) {
  return (
    <Container className="flex-col">
      <NextSeo title={title} description={description} />
      <section className="max-w-7xl mx-auto pb-11 lg:pb-52 pt-20 lg:pt-40">
        <div className="flex flex-col px-6 ">
          <h2 className="text-2xl md:text-4xl lg:text-5xl text-white font-medium w-3/4 z-10">
            {props.data.header_section.title}
          </h2>
          <div className="-mt-10 w-full">
            <div className="flex flex-col lg:flex-row -mx-6 lg:-mx-0">
              <div className="w-full lg:-ml-6 xl:-ml-28 lg:mr-28">
                <Image
                  src="/images/growers-page-header.jpg"
                  alt="MaryAG Growers"
                  width={812}
                  height={608}
                  layout="responsive"
                  objectFit="cover"
                  className="aspect-w-13 aspect-h-10"
                />
              </div>
              <div className="lg:w-1/3 lg:self-end px-6 lg:px-0">
                <h3 className="text-white text-base font-medium pb-14 lg:pb-16 pt-16 lg:pt-0">
                  {props.data.header_section.sub_title}
                </h3>
                <p className="text-white text-[20px] lg:text-lg pb-14 lg:pb-16">
                  {props.data.header_section.body}
                </p>
                <p className="text-white text-base text-opacity-70">
                  {props.data.header_section.sub_body}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="bg-taupe-300">
        <div className="py-6 md:px-6 max-w-7xl mx-auto md:py-16">
          <h3 className="px-6 text-grey-700 text-xl font-medium leading-10 mb-12 md:text-2xl md:pl-32 lg:w-1/2">
            See What Our Users Are Saying
          </h3>
          <TestimonialsSlider numberOfItems={props.data.testimonials?.length}>
            {props.data.testimonials?.map(
              (x: TestimonialFrontMatter, i: number) => (
                <div
                  className="flex-shrink-0 w-full pb-4 px-4 md:px-32"
                  key={x.name.concat(String(i))}
                >
                  <GrowerTestimonial {...x} src={x.image} />
                </div>
              )
            )}
          </TestimonialsSlider>
        </div>
      </div>

      <section className="py-16 lg:py-52 px-6 mx-auto max-w-7xl">
        <h2 className="text-white text-xl lg:text-3xl w-1/2 lg:w-auto font-medium pb-12 lg:pb-20">
          {props.data.knowledge_section.title}
        </h2>
        <ArticleList posts={props.data.knowledge_section.posts} />
      </section>
    </Container>
  )
}

async function getFilteredSortedPosts() {
  const posts = await getAllFilesFrontMatter('testimonials')

  return posts.sort((a, b) => a.order - b.order)
}

async function getFilteredSortedArticles() {
  const posts = await getAllFilesFrontMatter('articles')

  return posts
    .filter(
      (post: PostFrontMatter) =>
        process.env.NODE_ENV === 'development' || !!post.publishedAt
    )
    .sort(
      ({ publishedAt: firstDate }, { publishedAt: secondDate }) =>
        Number(new Date(secondDate)) - Number(new Date(firstDate))
    )
}
